import { KnownSortOrder } from '@groundfloor-platform/mgmt-service-g-sdk';
import { InfiniteScrollProps } from '../../../../ui/components/pure/list-table-wrappers/infinite-table.component';
import useSearchParams, { UseSearchParamsOptions } from '../../../../hooks/use-search-params.hook';
import { useInfiniteQueryCouriersList } from '../../../couriers/couriers.queries';
import { useQueryGetCarrier } from '../../carriers.queries';
import { useQueryGetProjectOptional } from '../../../../core/queries/projects.queries';
import {
  carrierCapabilitiesScopeProjectID,
  carrierCouriersScopeProjectID,
  CARRIER_COURIER_STATUS_WORKING,
  CarrierCourierStatusFilters,
  CarrierCouriersControlsState
} from '../logic/carrier-couriers-query.map';

const initialControlsState: CarrierCouriersControlsState = {
  sortBy: 'firstName',
  sortOrder: KnownSortOrder.Asc,
  status: CARRIER_COURIER_STATUS_WORKING
};

const searchParamOptions: UseSearchParamsOptions<CarrierCouriersControlsState> = {
  parse: { excludedValues: [''], convertBooleans: true },
  stringify: { excludedValues: [null, ''], stringifyDates: true }
};

export function useCarrierDetails(carrierID: string, projectID: string) {
  // The page's project type decides the capabilities' scope, so the carrier waits for it. Already
  // cached by the navigation bar; a project that fails to load falls back to scoping by its ID.
  const project = useQueryGetProjectOptional(projectID);
  const carrier = useQueryGetCarrier(project.isLoading ? undefined : carrierID, {
    projectID: carrierCapabilitiesScopeProjectID(projectID, project.data?.projectType),
    includeCapabilities: true
  });

  const [formState, queryState, onFormInput] = useSearchParams(initialControlsState, searchParamOptions);

  // Held until the carrier loads, so the list is never first asked of a project it cannot match.
  const couriersProjectID = carrier.data ? carrierCouriersScopeProjectID(carrier.data) || projectID : undefined;

  // The query map widens a carrier filter to its regions on its own.
  const couriers = useInfiniteQueryCouriersList(couriersProjectID, {
    groupID: carrierID,
    // A combined option stands for several statuses; a single one is already what it sends.
    status: queryState.status ? CarrierCourierStatusFilters[queryState.status] ?? queryState.status : undefined,
    sortBy: queryState.sortBy,
    sortOrder: queryState.sortOrder
  });

  const infiniteScroll: InfiniteScrollProps = {
    hasNextPage: couriers.hasNextPage || false,
    isFetchingNextPage: couriers.isFetchingNextPage,
    fetchNextPage: couriers.fetchNextPage,
    onScrollToBottom() {
      couriers.fetchNextPage();
    }
  };

  const isLoading = project.isLoading || carrier.isLoading;

  return { carrier, isLoading, couriers, infiniteScroll, formState, onFormInput } as const;
}
